import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { fieldEditBegin } from "../RecommendationEditor/redux/actions";
import { COMPLETED, ERROR, ERROR_404, IN_PROGRESS } from "../../reducers/currentMonitoredUri";
import { formatDateTime, formatDateTimeUtc, formatDisplayDate } from "./commons";
import { Field, ListField } from "./Field";
import RedirectChain from "./RedirectChain";
import IndexabilityField from "./IndexabilityField";
import CrawlScopeField from "./CrawlScopeField";
import { IndexablePill, ScopePill, OrphanBadge, HttpStatusBadge } from "./Pills";
import { useStaleness } from "./useStaleness";
import LastCrawledChip from "./LastCrawledChip";
import AgoChip from "./AgoChip";


function overlayText(fetchStatus) {
    switch (fetchStatus) {
        case IN_PROGRESS: return "Loading...";
        case COMPLETED:   return "";
        case ERROR_404:   return "Crawl not present for this day";
        case ERROR:       return "Unable to load the crawl for this day";
        default:          return "Error";
    }
}

export default function Details({ values, onGoInbound }) {
    const dispatch = useDispatch();
    const { id, uri, currentValue, recommendation, curSnapshotDate, lastSnapshotDate } = useSelector(s => s.currentMonitoredUri);
    const isPast = curSnapshotDate && lastSnapshotDate && curSnapshotDate !== lastSnapshotDate;
    const { isStale } = useStaleness();
    const [chainExpanded, setChainExpanded] = useState(false);

    const { cur, ref } = values;
    const { crawlScope } = cur;
    const redirectElements = cur?.redirectChainElements;
    const safeSitemapLinks = cur?.lastCrawl?.inboundLinks?.internal?.SITEMAP || [];

    const ahref    = cur?.lastCrawl?.inboundLinksCount?.internal?.ahref;
    const sitemaps = cur?.lastCrawl?.inboundLinks?.internal?.SITEMAP || [];
    const hasCrawl   = ahref > 0;
    const hasSitemap = sitemaps.length > 0;

    const beginEdit = (isArrayField) => (fieldId) => {
        dispatch(fieldEditBegin(id, uri, fieldId, isArrayField, currentValue[fieldId], recommendation[fieldId]));
    };

    const beginEditDestinationUri = (fieldId) => {
        dispatch(fieldEditBegin(id, uri, fieldId, false, currentValue.destinationUri, recommendation[fieldId]));
    };

    const indexable  = cur?.indexability?.indexable;
    const crawlStatus = cur?.crawlStatus;
    const finalHttpStatus = Array.isArray(redirectElements) && redirectElements.length > 0
        ? redirectElements[redirectElements.length - 1]?.httpStatus
        : null;
    const isNon200 = finalHttpStatus && parseInt(finalHttpStatus, 10) !== 200;

    const blockedByRobots = (cur?.indexability?.notIndexableReasons || []).includes("BLOCKED_BY_ROBOTS_TXT");
    const hasRedirectChain = Array.isArray(redirectElements) && redirectElements.length > 0;
    const notCrawledByRobots = cur.fetchStatus === COMPLETED && blockedByRobots && !hasRedirectChain;
    const noCrawl = cur.fetchStatus === ERROR_404;

    return (
        <Wrap $past={isPast}>
            {cur.fetchStatus !== COMPLETED && cur.fetchStatus !== ERROR_404 && (
                <Overlay>
                    {cur.fetchStatus === IN_PROGRESS && <Spinner />}
                    {overlayText(cur.fetchStatus) && <OverlayText>{overlayText(cur.fetchStatus)}</OverlayText>}
                </Overlay>
            )}
            {!noCrawl && <SummaryStrip $past={isPast}>
                <SummaryPills>
                    {finalHttpStatus && <HttpStatusBadge code={finalHttpStatus} />}
                    {!isNon200 && <IndexablePill indexable={indexable} />}
                    <ScopePill crawlScope={crawlScope} />
                    {crawlStatus && <HttpStatusBadge code={crawlStatus} />}
                </SummaryPills>
                <SummaryDiscovery>
                    <DiscoveryLabel>Discovered via</DiscoveryLabel>
                    {!hasCrawl && !hasSitemap ? (
                        <OrphanBadge>Orphan page</OrphanBadge>
                    ) : (
                        <>
                            <DiscoveryCheck $on={hasCrawl} $clickable={hasCrawl}
                                title={hasCrawl ? `${ahref} internal link${ahref !== 1 ? 's' : ''}` : 'Not found via crawl'}
                                onClick={hasCrawl ? onGoInbound : undefined}>
                                Crawl{hasCrawl ? ` · ${ahref}` : ''}
                            </DiscoveryCheck>
                            <DiscoverySep>·</DiscoverySep>
                            <DiscoveryCheck $on={hasSitemap}
                                title={hasSitemap ? sitemaps.join('\n') : 'Not in any sitemap'}>
                                Sitemap{hasSitemap && sitemaps.length > 1 ? ` (${sitemaps.length})` : ''}
                            </DiscoveryCheck>
                        </>
                    )}
                </SummaryDiscovery>
            </SummaryStrip>}
            {cur.createDate && (
                <Field diffDisabled fieldName="Crawl date" fieldValueCur={formatDateTimeUtc(cur.createDate)}>
                    <UtcBadge>
                        UTC
                        <Tooltip>
                            <TooltipTitle>This time is shown in a different timezone from yours.</TooltipTitle>
                            <TooltipLocal>In your timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}), this page was crawled at</TooltipLocal>
                            <TooltipTime>{formatDateTime(cur.createDate)}</TooltipTime>
                        </Tooltip>
                    </UtcBadge>
                    {isStale && <StaleChip createDate={cur.createDate} />}
                    {isPast && <SnapshotAgeChip date={cur.createDate} />}
                </Field>
            )}
            {noCrawl && <Field diffDisabled fieldName="Crawl date" fieldValueCur={`None on ${formatDisplayDate(curSnapshotDate)}`} />}
            <Field diffDisabled fieldName="Source URL" fieldValueCur={uri} />
            <CrawlValues>
                {noCrawl && (
                    <NotCrawledOverlay>
                        <NotCrawledMessage>{overlayText(ERROR_404)}</NotCrawledMessage>
                    </NotCrawledOverlay>
                )}
                <CrawledFields>
                    {notCrawledByRobots && (
                        <NotCrawledOverlay>
                            <NotCrawledMessage>
                                This page was not crawled. Reason: {cur.crawlStatus}
                            </NotCrawledMessage>
                        </NotCrawledOverlay>
                    )}
                    <Field beginEdit={beginEditDestinationUri} fieldName="Destination URL" fieldId="destinationUri"
                        fieldValueCur={cur.destinationUri} fieldValueRef={ref.destinationUri} />
                    {redirectElements && (
                        <ChainRow>
                            <ChainRowLabel>Redirect Chain</ChainRowLabel>
                            <ChainBody>
                                <ChainSummary>
                                    <ChainCodes>
                                        {redirectElements.map((el, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && <ChainArrow>→</ChainArrow>}
                                                <HttpStatusBadge code={el.httpStatus} />
                                            </React.Fragment>
                                        ))}
                                    </ChainCodes>
                                    <ExpandBtn onClick={() => setChainExpanded(e => !e)}>
                                        {chainExpanded ? 'Hide URLs' : 'Show URLs'}
                                    </ExpandBtn>
                                </ChainSummary>
                                {chainExpanded && <RedirectChain elements={redirectElements} crawlStatus={cur.crawlStatus} />}
                            </ChainBody>
                        </ChainRow>
                    )}
                    <Field beginEdit={beginEdit(false)} fieldName="Title" fieldId="title"
                        fieldValueCur={cur.title} fieldValueRef={ref.title} />
                    <ListField beginEdit={beginEdit(true)} fieldName="Meta Description" fieldId="metaDescriptions"
                        fieldValueCur={cur.metaDescriptions} fieldValueRef={ref.metaDescriptions} />
                    <ListField beginEdit={beginEdit(true)} fieldName="H1s" fieldId="h1s"
                        fieldValueCur={cur.h1s} fieldValueRef={ref.h1s} />
                    <ListField diffDisabled fieldName="H2s" fieldId="h2s"
                        fieldValueCur={cur.h2s} fieldValueRef={ref.h2s} />
                    <ListField diffDisabled fieldName="Canonicals" fieldId="canonicals"
                        fieldValueCur={cur.canonicals} fieldValueRef={cur.canonicals} baseUrl={uri} />
                </CrawledFields>
                <IndexabilityField cur={cur} />
                <CrawlScopeField crawlScope={crawlScope} />
                <ListField diffDisabled noUriLink fieldName="Sitemap Links" fieldId="sitemaps"
                    fieldValueCur={safeSitemapLinks} fieldValueRef={safeSitemapLinks} />
                <Field diffDisabled fieldName="Crawl Status" fieldValueCur={cur.crawlStatus} />
            </CrawlValues>
        </Wrap>
    );
}

const Wrap = styled.div`
    position: relative;
    background: ${({ $past }) => $past ? '#fffdf0' : 'transparent'};
`;

const CrawlValues = styled.div`
    position: relative;
`;

const CrawledFields = styled.div`
    position: relative;
`;

const StaleChip = styled(LastCrawledChip)`
    margin-left: 16px;
`;

const SnapshotAgeChip = styled(AgoChip)`
    margin-left: 16px;
`;

const NotCrawledOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(248, 250, 252, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    z-index: 5;
`;

const NotCrawledMessage = styled.div`
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    background: #fff;
    padding: 12px 20px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    text-align: center;
    max-width: 90%;
`;

const TooltipTitle = styled.span`
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 5px;
    line-height: 1.4;
`;

const TooltipLocal = styled.span`
    display: block;
    font-size: 0.7rem;
    font-weight: 400;
    color: #94a3b8;
    line-height: 1.4;
`;

const TooltipTime = styled.span`
    display: block;
    color: #e2e8f0;
    font-weight: 600;
    font-size: 0.72rem;
    margin-top: 3px;
`;

const Tooltip = styled.span`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    width: 260px;
    background: #1e293b;
    padding: 10px 14px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
    pointer-events: none;
    opacity: 0;
    transition: opacity 100ms;
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-top-color: #1e293b;
    }
`;

const UtcBadge = styled.span`
    position: relative;
    font-size: 0.6rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
    margin-left: 4px;
    cursor: help;
    &:hover ${Tooltip} { opacity: 1; }
`;

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.75);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 10;
`;

const Spinner = styled.div`
    width: 24px;
    height: 24px;
    border: 3px solid #e2e8f0;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    @keyframes spin { to { transform: rotate(360deg); } }
`;

const OverlayText = styled.div`
    font-size: 0.78rem;
    color: #64748b;
    font-weight: 500;
`;

const ChainRow = styled.div`
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 12px;
    padding: 12px 24px;
    border-bottom: 1px solid #f1f5f9;
    align-items: start;
    font-family: "Inter", system-ui, sans-serif;
    font-size: 0.85rem;
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        padding: 10px 14px;
        gap: 6px;
    }
`;

const ChainRowLabel = styled.div`
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    padding-top: 2px;
`;

const ChainBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ChainSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ChainCodes = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ChainArrow = styled.span`
    font-size: 0.65rem;
    color: #cbd5e1;
`;


const ExpandBtn = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 0.7rem;
    font-weight: 600;
    color: #6366f1;
    cursor: pointer;
    &:hover { text-decoration: underline; }
`;

const SummaryStrip = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 24px 12px;
    background: ${({ $past }) => $past ? '#fef9c3' : '#f8fafc'};
    font-family: "Inter", system-ui, sans-serif;
    @media (max-width: 600px) { padding: 10px 14px; }
`;

const SummaryPills = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

const SummaryDiscovery = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;


const DiscoveryLabel = styled.span`
    font-size: 0.68rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding-right: 6px;
    border-right: 1px solid #e2e8f0;
    white-space: nowrap;
    flex-shrink: 0;
`;

const DiscoveryCheck = styled.span`
    font-size: 0.72rem;
    font-weight: 600;
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
    color: ${({ $on }) => $on ? '#0f172a' : '#cbd5e1'};
    white-space: nowrap;
    ${({ $clickable }) => $clickable && `&:hover { color: #6366f1; text-decoration: underline; }`}
`;

const DiscoverySep = styled.span`
    color: #e2e8f0;
    font-size: 0.7rem;
`;


